import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { solutions, services } from "@/mocks/homeData";
import LazyBackgroundVideo from "@/components/feature/LazyBackgroundVideo";

interface ServicesProps {
  onOpenModal: (type: string) => void;
}

function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return { ref, isVisible };
}

export default function Services({ onOpenModal }: ServicesProps) {
  const { t } = useTranslation("common");
  const solRef = useScrollReveal<HTMLDivElement>();
  const svcRef = useScrollReveal<HTMLDivElement>();

  return (
    <section id="services" className="relative overflow-hidden">
      {/* ===== Video Background Layer ===== */}
      <div className="absolute inset-0 z-0">
        <LazyBackgroundVideo
          src="https://cdn.pixabay.com/video/2024/03/15/204172-922723023_large.mp4"
          poster="https://readdy.ai/api/search-image?query=Futuristic%20cybersecurity%20operations%20center%20with%20diverse%20team%20of%20IT%20professionals%20collaborating%2C%20modern%20glass-walled%20office%2C%20holographic%20security%20dashboards%2C%20warm%20ambient%20lighting%2C%20professional%20technology%20workplace%20atmosphere%2C%20high-end%20editorial%20photography%2C%20cinematic%20depth%20of%20field%2C%20diverse%20multiethnic%20team&width=1600&height=900&seq=services-video-poster&orientation=landscape"
          posterSrcSet="https://readdy.ai/api/search-image?query=Futuristic%20cybersecurity%20operations%20center%20with%20diverse%20team%20of%20IT%20professionals%20collaborating%2C%20modern%20glass-walled%20office%2C%20holographic%20security%20dashboards%2C%20warm%20ambient%20lighting%2C%20professional%20technology%20workplace%20atmosphere%2C%20high-end%20editorial%20photography%2C%20cinematic%20depth%20of%20field%2C%20diverse%20multiethnic%20team&width=1280&height=720&seq=services-video-poster&orientation=landscape 1280w, https://readdy.ai/api/search-image?query=Futuristic%20cybersecurity%20operations%20center%20with%20diverse%20team%20of%20IT%20professionals%20collaborating%2C%20modern%20glass-walled%20office%2C%20holographic%20security%20dashboards%2C%20warm%20ambient%20lighting%2C%20professional%20technology%20workplace%20atmosphere%2C%20high-end%20editorial%20photography%2C%20cinematic%20depth%20of%20field%2C%20diverse%20multiethnic%20team&width=1600&height=900&seq=services-video-poster&orientation=landscape 1600w"
        />
        {/* Deep overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1220]/95 via-[#0A1220]/88 to-[#0A1220]/95" />
        {/* Tech grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(oklch(var(--primary-500) / 0.5) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        {/* Radial glows */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-primary-500/8 blur-[180px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-accent-500/6 blur-[150px]" />
      </div>

      {/* ====== SOLUCIONES ====== */}
      <div className="relative z-10 py-12 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          {/* Header */}
          <div
            ref={solRef.ref}
            className={`text-center mb-8 md:mb-12 transition-all duration-1000 ${solRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary-400/20 bg-primary-500/10 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
              <span className="text-[10px] font-label font-semibold text-primary-300 uppercase tracking-[0.22em]">
                {t("services.tag")}
              </span>
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-50 mb-3">
              {t("services.title")}{" "}
              <span className="bg-gradient-to-r from-primary-300 via-primary-400 to-accent-400 bg-clip-text text-transparent">
                {t("services.highlight")}
              </span>
            </h2>
            <p className="text-sm text-foreground-400/70 max-w-xl mx-auto leading-relaxed">
              {t("services.subtitle")}
            </p>
          </div>

          {/* Solutions grid - 3x2 glassmorphic cards */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {solutions.map((solution, idx) => (
                <div
                  key={solution.id}
                  className={`transition-all duration-700 ${solRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                  style={{ transitionDelay: `${120 + idx * 90}ms` }}
                >
                  <SolutionCardGlass solution={solution} onOpenModal={onOpenModal} t={t} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ====== DIVIDER ====== */}
      <div className="relative z-10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-400/30 to-primary-400/20" />
            <div className="flex items-center gap-3 px-5 py-2 rounded-full border border-primary-400/15 bg-primary-500/8">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full border-2 border-background-950 bg-primary-500/30 flex items-center justify-center"
                  >
                    <i className="ri-user-line text-[10px] text-primary-200" />
                  </div>
                ))}
              </div>
              <span className="text-[11px] font-label text-primary-300/80 tracking-wide whitespace-nowrap">
                {t("services.specialistsBar")}
              </span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-primary-400/30 to-primary-400/20" />
          </div>
        </div>
      </div>

      {/* Transition gradient toward the next (light) section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-b from-transparent to-[#F0F4FA] z-[5] pointer-events-none" />

      {/* ====== SERVICIOS ====== */}
      <div className="relative z-10 py-12 md:py-16">
        {/* Humanized background for the services block */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Diverse%20team%20of%20cybersecurity%20analysts%20collaborating%20around%20computers%20in%20a%20modern%20dark%20operations%20room%2C%20warm%20ambient%20lighting%2C%20engaged%20and%20friendly%20atmosphere%2C%20professional%20editorial%20photography%2C%20cinematic%20depth%20of%20field%2C%20human%20centered&width=900&height=450&seq=services-block-human-bg-01&orientation=landscape"
            srcSet="https://readdy.ai/api/search-image?query=Diverse%20team%20of%20cybersecurity%20analysts%20collaborating%20around%20computers%20in%20a%20modern%20dark%20operations%20room%2C%20warm%20ambient%20lighting%2C%20engaged%20and%20friendly%20atmosphere%2C%20professional%20editorial%20photography%2C%20cinematic%20depth%20of%20field%2C%20human%20centered&width=480&height=240&seq=services-block-human-bg-01&orientation=landscape 480w, https://readdy.ai/api/search-image?query=Diverse%20team%20of%20cybersecurity%20analysts%20collaborating%20around%20computers%20in%20a%20modern%20dark%20operations%20room%2C%20warm%20ambient%20lighting%2C%20engaged%20and%20friendly%20atmosphere%2C%20professional%20editorial%20photography%2C%20cinematic%20depth%20of%20field%2C%20human%20centered&width=900&height=450&seq=services-block-human-bg-01&orientation=landscape 900w"
            sizes="100vw"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center opacity-25"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1220]/70 via-[#0A1220]/60 to-[#0A1220]/75" />
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10">
          <div
            ref={svcRef.ref}
            className={`text-center mb-8 md:mb-10 transition-all duration-1000 ${svcRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-50 mb-3">
              {t("services.servicesTitle")}{" "}
              <span className="bg-gradient-to-r from-accent-300 via-primary-400 to-primary-300 bg-clip-text text-transparent">
                {t("services.servicesHighlight")}
              </span>
            </h2>
            <p className="text-sm text-foreground-400/70 max-w-2xl mx-auto leading-relaxed">
              {t("services.servicesSubtitle")}
            </p>
          </div>

          {/* Services grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {services.map((service, idx) => (
              <div
                key={service.id}
                className={`transition-all duration-700 ${svcRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{ transitionDelay: `${150 + idx * 100}ms` }}
              >
                <ServiceCardGlass service={service} onOpenModal={onOpenModal} t={t} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ====== Glassmorphic Solution Card ====== */
function SolutionCardGlass({
  solution,
  onOpenModal,
  t,
}: {
  solution: { id: string; titleKey: string; subtitleKey: string; icon: string; modalType: string; image: string; imageSrcSet: string };
  onOpenModal: (type: string) => void;
  t: (key: string) => string;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenModal(solution.modalType)}
      className="group relative overflow-hidden rounded-2xl border border-primary-400/10 bg-background-900/40 backdrop-blur-md hover:border-primary-400/30 hover:bg-background-900/60 transition-all duration-500 cursor-pointer text-left w-full flex flex-col"
    >
      {/* Gradient top accent line */}
      <div className="absolute top-0 left-3 right-3 h-px">
        <div className="h-full w-10 bg-gradient-to-r from-primary-400 to-accent-400 opacity-30 group-hover:w-full group-hover:opacity-80 transition-all duration-700" />
      </div>

      {/* Image area */}
      <div className="relative h-36 md:h-40 overflow-hidden">
        <img
          src={solution.image}
          alt={t(solution.titleKey)}
          width={400}
          height={180}
          srcSet={solution.imageSrcSet}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 384px"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-900/80 via-background-900/20 to-transparent" />
        {/* Icon floating over image */}
        <div className="absolute bottom-3 left-4 w-10 h-10 flex items-center justify-center rounded-xl bg-background-900/70 backdrop-blur-sm border border-primary-400/20 group-hover:bg-primary-500/20 group-hover:border-primary-400/40 transition-all duration-500">
          <i className={`${solution.icon} text-lg text-primary-300 group-hover:text-primary-200`} />
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-100 group-hover:text-foreground-50 transition-colors duration-300 mb-1.5">
          {t(solution.titleKey)}
        </h3>
        <p className="text-xs text-foreground-400/60 leading-relaxed mb-4 flex-1">
          {t(solution.subtitleKey)}
        </p>

        {/* Bottom action */}
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-foreground-100/6">
          <span className="text-[11px] font-label font-semibold text-primary-400/70 group-hover:text-primary-300 transition-colors duration-300 uppercase tracking-wider">
            {t("services.viewSolution")}
          </span>
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500/10 group-hover:bg-primary-500/25 transition-all duration-300">
            <i className="ri-arrow-right-line text-xs text-primary-400 group-hover:text-primary-200 group-hover:translate-x-0.5 transition-all duration-300" />
          </div>
        </div>
      </div>

      {/* Corner accent */}
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-primary-400/0 group-hover:border-primary-400/20 transition-all duration-500 rounded-br-sm" />
    </button>
  );
}

/* ====== Glassmorphic Service Card ====== */
function ServiceCardGlass({
  service,
  onOpenModal,
  t,
}: {
  service: { id: string; titleKey: string; subtitleKey: string; icon: string; modalType: string; itemKeys: string[] };
  onOpenModal: (type: string) => void;
  t: (key: string) => string;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenModal(service.modalType)}
      className="group relative overflow-hidden rounded-2xl border border-foreground-100/6 bg-background-900/40 backdrop-blur-md hover:border-accent-400/25 hover:bg-background-900/60 transition-all duration-500 cursor-pointer text-left w-full flex flex-col"
    >
      {/* Gradient top accent line */}
      <div className="absolute top-0 left-3 right-3 h-px">
        <div className="h-full w-8 bg-gradient-to-r from-accent-400 to-primary-400 opacity-20 group-hover:w-full group-hover:opacity-70 transition-all duration-700" />
      </div>

      <div className="p-5 md:p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500/10 border border-accent-400/15 flex-shrink-0 group-hover:bg-accent-500/20 group-hover:border-accent-400/30 transition-all duration-500">
            <i className={`${service.icon} text-xl text-accent-300 group-hover:text-accent-200`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-100 group-hover:text-foreground-50 transition-colors duration-300 leading-tight">
              {t(service.titleKey)}
            </h3>
            <p className="text-xs text-foreground-400/60 mt-1 leading-relaxed">
              {t(service.subtitleKey)}
            </p>
          </div>
        </div>

        {/* Items list */}
        <div className="space-y-1.5 mb-5 flex-1">
          {service.itemKeys.slice(0, 4).map((itemKey) => (
            <div key={itemKey} className="flex items-center gap-2 text-xs text-foreground-400/50 group-hover:text-foreground-400/70 transition-colors">
              <span className="w-1 h-1 rounded-full bg-accent-400/35 flex-shrink-0 group-hover:bg-accent-400/60 transition-colors" />
              <span className="line-clamp-1">{t(itemKey)}</span>
            </div>
          ))}
          {service.itemKeys.length > 4 && (
            <div className="flex items-center gap-2 text-xs text-accent-400/50">
              <span className="w-1 h-1 rounded-full bg-accent-400/35 flex-shrink-0" />
              <span>+{service.itemKeys.length - 4} {t("services.moreServices")}</span>
            </div>
          )}
        </div>

        {/* Bottom action */}
        <div className="flex items-center gap-2 text-accent-400/50 group-hover:text-accent-300 group-hover:gap-3 transition-all duration-300 mt-auto pt-3 border-t border-foreground-100/5">
          <span className="text-xs font-label font-semibold uppercase tracking-widest">{t("services.viewService")}</span>
          <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300" />
        </div>
      </div>

      {/* Corner accent */}
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-accent-400/0 group-hover:border-accent-400/15 transition-all duration-500 rounded-br-sm" />
    </button>
  );
}